<template>
  <div class="tab-content">
    <section class="settings-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        </div>
        <div>
          <h3 class="card-title">Presenças</h3>
          <p class="card-desc">Define o horário padrão sugerido ao marcar presença dos funcionários.</p>
        </div>
      </div>
      <div class="fields-row">
        <div class="field-wrap">
          <label class="field-label" for="attendance-start-time">Horário padrão de entrada</label>
          <input id="attendance-start-time" class="field-input" :value="form.attendanceStartTime" type="time"
            @input="$emit('update-form', { field: 'attendanceStartTime', value: ($event.target as HTMLInputElement).value })" />
          <p class="field-hint">Esse valor aparece como sugestão ao marcar alguém como presente.</p>
        </div>
        <div class="field-wrap">
          <label class="field-label">Dias da semana trabalhados</label>
          <div class="weekday-grid">
            <button v-for="day in WEEKDAYS" :key="day.value" class="weekday-chip" :class="{ active: isWorked(day.value) }" type="button" @click="toggleDay(day.value)">
              <span class="weekday-chip__short">{{ day.short }}</span>
              <span class="weekday-chip__label">{{ day.label }}</span>
            </button>
          </div>
          <p class="field-hint">Dias fora dessa lista aparecem como opcionais no gerenciamento de usuários.</p>
        </div>
      </div>
    </section>
    <div class="save-row">
      <button class="btn-save" type="button" @click="$emit('save')">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
        Salvar configurações
      </button>
      <Transition name="fade"><span v-if="successText" class="save-success">{{ successText }}</span></Transition>
      <p v-if="errorText" class="save-error">{{ errorText }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import type { SystemConfig } from '@/store/system'

const WEEKDAYS = [
  { value: 'mon', short: 'Seg', label: 'Segunda' }, { value: 'tue', short: 'Ter', label: 'Terca' },
  { value: 'wed', short: 'Qua', label: 'Quarta' }, { value: 'thu', short: 'Qui', label: 'Quinta' },
  { value: 'fri', short: 'Sex', label: 'Sexta' }, { value: 'sat', short: 'Sab', label: 'Sabado' },
  { value: 'sun', short: 'Dom', label: 'Domingo' },
] as const

export default defineComponent({
  name: 'SystemTabPresencas',
  props: { form: { type: Object as PropType<SystemConfig>, required: true }, errorText: { type: String, default: '' }, successText: { type: String, default: '' } },
  emits: ['update-form', 'save'],
  setup(props, { emit }) {
    const isWorked = (v: string) => Array.isArray(props.form.workedWeekdays) && props.form.workedWeekdays.includes(v)
    const toggleDay = (v: string) => {
      const cur = Array.isArray(props.form.workedWeekdays) ? [...props.form.workedWeekdays] : []
      emit('update-form', { field: 'workedWeekdays', value: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v] })
    }
    return { WEEKDAYS, isWorked, toggleDay }
  }
})
</script>

<style scoped lang="scss">
.tab-content { display: flex; flex-direction: column; gap: 16px; }
.settings-card { border: 1px solid rgba(31,30,26,.1); border-radius: 14px; background: var(--card,#fff); padding: 22px 24px; display: flex; flex-direction: column; gap: 20px; }
.card-header { display: flex; align-items: flex-start; gap: 14px; padding-bottom: 16px; border-bottom: 1px solid rgba(31,30,26,.07); }
.card-icon { width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0; background: rgba(31,30,26,.06); display: flex; align-items: center; justify-content: center; }
.card-title { margin: 0 0 2px; font-size: 15px; font-weight: 700; color: var(--ink,#2c2b27); }
.card-desc { margin: 0; font-size: 12px; color: var(--ink-muted,#888); }
.fields-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.field-wrap { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 11px; font-weight: 700; color: var(--ink-muted,#888); text-transform: uppercase; letter-spacing: .05em; }
.field-input { padding: 9px 12px; border-radius: 8px; border: 1.5px solid rgba(31,30,26,.16); font-size: 14px; color: var(--ink,#2c2b27); background: #fff; outline: none; width: 100%; box-sizing: border-box; &:focus { border-color: var(--system-navbar,#0f4c81); } }
.field-hint { font-size: 11px; color: var(--ink-muted,#aaa); margin: 0; }
.weekday-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(118px, 1fr)); gap: 10px; }
.weekday-chip { display: flex; align-items: center; gap: 10px; padding: 11px 12px; border-radius: 10px; border: 1.5px solid rgba(31,30,26,.12); background: #fff; cursor: pointer; &.active { border-color: var(--system-navbar,#0f4c81); background: rgba(15,76,129,.08); } }
.weekday-chip__short { min-width: 36px; height: 36px; border-radius: 10px; background: rgba(31,30,26,.06); display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; .weekday-chip.active & { background: var(--system-navbar,#0f4c81); color: #fff; } }
.weekday-chip__label { font-size: 13px; font-weight: 700; color: var(--ink,#2c2b27); }
.save-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.btn-save { display: inline-flex; align-items: center; gap: 8px; padding: 11px 24px; border-radius: 10px; border: none; cursor: pointer; background: var(--system-navbar,#0f4c81); color: #fff; font-size: 14px; font-weight: 700; &:hover { opacity: .88; } }
.save-success { font-size: 13px; font-weight: 600; color: #15803d; }
.save-error { margin: 0; font-size: 13px; color: #dc2626; }
.fade-enter-active, .fade-leave-active { transition: opacity .3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
@media (max-width: 800px) { .fields-row { grid-template-columns: 1fr; } .settings-card { padding: 16px; } }
</style>
